import { css } from '@landr/styled-system/css';
import React, { PropsWithChildren } from 'react';
import { Tabs, Divider, Group } from '@landr/maestro';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSearchController } from 'controllers/SearchController/SearchContext';
import { Slice } from 'components/Slice';
import SamplesPane from './components/SamplesPane';
import PacksPane from './components/PacksPane';
import Appended from './components/Appended';
import useDetailsView from './useDetailsView';
import DropdownSorting from './components/DropdownSorting';

const tabsStyles = css({
    maxWidth: '100%',
    '& nav': {
        overflow: 'initial',
    },
    '& [role="tabpanel"]': {
        paddingTop: 0,
    },
});

const getMainStyles = () =>
    css({
        width: '100%',
        minHeight: '500px',
        maxWidth: {
            base: '100%',
            md: '100%',
        },
    });

interface SubComponents {
    SamplesPane: typeof SamplesPane;
    PacksPane: typeof PacksPane;
    DropdownSorting: typeof DropdownSorting;
}

export type JSXElementConstructorWithKey = React.JSXElementConstructor<any> & {
    key?: string;
};

const DetailsView: React.FC<PropsWithChildren> & SubComponents = ({
    children,
}) => {
    const navigate = useNavigate();
    const { search, pathname } = useLocation();
    const { activeTabTotal } = useSearchController();

    const { activeTab, handleSelectTab, firstTab, secondTab, showTabs } =
        useDetailsView({
            children,
            search,
            pathname,
            navigate,
        });

    return (
        <Slice>
            <Group gap="none">
                <Group.Item className={getMainStyles()}>
                    {!showTabs && (
                        <>
                            <Divider />
                            {firstTab?.content}
                        </>
                    )}

                    {showTabs && (
                        <Tabs
                            className={tabsStyles}
                            selectedTab={activeTab}
                            onSelectTab={
                                handleSelectTab as (tabName: string) => void
                            }
                            size="lg"
                            isLazy={true}
                        >
                            {firstTab.content && (
                                <Tabs.Pane
                                    name={firstTab.name}
                                    label={firstTab.displayName}
                                    pt={firstTab?.pt}
                                >
                                    {firstTab.content}
                                </Tabs.Pane>
                            )}

                            {secondTab.content && (
                                <Tabs.Pane
                                    name={secondTab.name}
                                    label={secondTab.displayName}
                                    pt={secondTab?.pt}
                                >
                                    {secondTab.content}
                                </Tabs.Pane>
                            )}
                        </Tabs>
                    )}
                </Group.Item>
            </Group>

            <Appended
                activeTab={activeTab}
                activeTabTotal={activeTabTotal}
                firstTab={firstTab}
                secondTab={secondTab}
            />
        </Slice>
    );
};

DetailsView.SamplesPane = SamplesPane;
DetailsView.PacksPane = PacksPane;
DetailsView.DropdownSorting = DropdownSorting;

export default DetailsView;
